import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../utils/axiosInstance';
import Swal from 'sweetalert2';
import Select from 'react-select';
import * as XLSX from 'xlsx';

// ─── helpers ────────────────────────────────────────────────────────────────

function to12h(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

function formatDisplayDate(ymd) {
  if (!ymd) return '';
  const [y, mo, d] = ymd.split('-').map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString('en-GB', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });
}

function todayYMD() {
  return new Date().toISOString().slice(0, 10);
}

const BLANK_FORM = {
  classroomId: '',
  date: '',
  startTime: '',
  endTime: '',
  title: '',
  description: '',
  organizer: '',
  contactNo: '',
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    fontSize: '0.875rem',
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#fb7185' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(251,113,133,0.25)' : 'none',
    '&:hover': { borderColor: '#fb7185' },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected ? '#f43f5e' : state.isFocused ? '#fff1f2' : 'white',
    color: state.isSelected ? 'white' : '#111827',
  }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '0.5rem', fontSize: '0.875rem' }),
  placeholder: (base) => ({ ...base, color: '#9ca3af' }),
  singleValue: (base) => ({ ...base, color: '#111827' }),
};

// ─── component ──────────────────────────────────────────────────────────────

export default function ClassroomBlocksModal({ isOpen, onClose, defaultDate = '' }) {
  const [activeTab, setActiveTab] = useState('blocks');

  // ── Blocks tab state ──
  const [blocks, setBlocks] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDate, setFilterDate] = useState(defaultDate || '');
  const [filterClassroomId, setFilterClassroomId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...BLANK_FORM, date: defaultDate || '' });
  const [formErrors, setFormErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  // ── Report tab state ──
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState(todayYMD());
  const [reportBlocks, setReportBlocks] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFetched, setReportFetched] = useState(false);

  // ── data loaders ──────────────────────────────────────────────────────────

  const loadClassrooms = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/classrooms/list');
      setClassrooms(Array.isArray(res.data) ? res.data : []);
    } catch {
      setClassrooms([]);
    }
  }, []);

  const loadBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDate) params.date = filterDate;
      if (filterClassroomId) params.classroomId = filterClassroomId;
      const res = await axiosInstance.get('/admin/classrooms/blocks', { params });
      setBlocks(res.data?.blocks || []);
    } catch {
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, [filterDate, filterClassroomId]);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    setReportFetched(false);
    try {
      const params = {};
      if (reportStartDate) params.startDate = reportStartDate;
      if (reportEndDate)   params.endDate   = reportEndDate;
      const res = await axiosInstance.get('/admin/classrooms/blocks', { params });
      const all = res.data?.blocks || [];
      // sort newest → oldest
      all.sort((a, b) => {
        const da = `${String(a.date).slice(0,10)}T${String(a.startTime).slice(0,5)}`;
        const db = `${String(b.date).slice(0,10)}T${String(b.startTime).slice(0,5)}`;
        return db.localeCompare(da);
      });
      setReportBlocks(all);
      setReportFetched(true);
    } catch {
      setReportBlocks([]);
      setReportFetched(true);
    } finally {
      setReportLoading(false);
    }
  }, [reportStartDate, reportEndDate]);

  useEffect(() => {
    if (isOpen) {
      loadClassrooms();
      loadBlocks();
    }
  }, [isOpen, loadClassrooms, loadBlocks]);

  useEffect(() => {
    if (isOpen) {
      setFilterDate(defaultDate || '');
      setForm((prev) => ({ ...prev, date: defaultDate || '' }));
    }
  }, [defaultDate, isOpen]);

  // reset report when tab switches away
  useEffect(() => {
    if (activeTab !== 'report') {
      setReportFetched(false);
      setReportBlocks([]);
    }
  }, [activeTab]);

  // ── form helpers ──────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...BLANK_FORM, date: filterDate || defaultDate || '' });
    setFormErrors([]);
    setShowForm(true);
  };

  const openEdit = (block) => {
    setEditingId(block.blockId);
    setForm({
      classroomId: String(block.classroomId),
      date: block.date ? String(block.date).slice(0, 10) : '',
      startTime: block.startTime ? String(block.startTime).slice(0, 5) : '',
      endTime: block.endTime ? String(block.endTime).slice(0, 5) : '',
      title: block.title || '',
      description: block.description || '',
      organizer: block.organizer || '',
      contactNo: block.contactNo || '',
    });
    setFormErrors([]);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...BLANK_FORM, date: filterDate || defaultDate || '' });
    setFormErrors([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setFormErrors([]);
    setSaving(true);
    try {
      const payload = {
        classroomId: form.classroomId ? Number(form.classroomId) : null,
        date: form.date,
        startTime: form.startTime,
        endTime: form.endTime,
        title: form.title.trim(),
        description: form.description.trim() || null,
        organizer: form.organizer.trim() || null,
        contactNo: form.contactNo.trim() || null,
      };
      if (editingId) {
        await axiosInstance.put(`/admin/classrooms/blocks/${editingId}`, payload);
      } else {
        await axiosInstance.post('/admin/classrooms/blocks', payload);
      }
      closeForm();
      await loadBlocks();
    } catch (err) {
      const errs = err.response?.data?.errors;
      if (Array.isArray(errs) && errs.length > 0) {
        setFormErrors(errs);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (blockId) => {
    const result = await Swal.fire({
      title: 'Delete this block?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/admin/classrooms/blocks/${blockId}`);
      await loadBlocks();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to delete block.', 'error');
    }
  };

  // ── Excel export ──────────────────────────────────────────────────────────

  const downloadExcel = () => {
    if (!reportBlocks.length) return;
    const rows = reportBlocks.map((b) => ({
      'Date': String(b.date).slice(0, 10),
      'Day': formatDisplayDate(String(b.date).slice(0, 10)),
      'Room': b.classroomName || '',
      'Floor': b.floor || '',
      'Start Time': to12h(String(b.startTime).slice(0, 5)),
      'End Time': to12h(String(b.endTime).slice(0, 5)),
      'Title': b.title || '',
      'Description': b.description || '',
      'Organizer': b.organizer || '',
      'Contact No': b.contactNo || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    // column widths
    ws['!cols'] = [10,28,18,10,12,10,32,40,24,16].map((w) => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Room Blocks');
    const from = reportStartDate || 'all';
    const to   = reportEndDate   || 'today';
    XLSX.writeFile(wb, `room-blocks_${from}_to_${to}.xlsx`);
  };

  // ── early return ──────────────────────────────────────────────────────────

  if (!isOpen) return null;

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* ── header ── */}
        <div className="bg-gradient-to-r from-rose-600 via-pink-600 to-rose-700 px-6 py-5 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 opacity-90">
              <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z" clipRule="evenodd" />
            </svg>
            <div>
              <h2 className="text-lg font-bold tracking-tight">Room Blocks</h2>
              <p className="text-rose-100 text-xs mt-0.5">Manage room unavailability for seminars, maintenance &amp; more</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-white/70 hover:bg-white/20 hover:text-white transition-all"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* ── tabs ── */}
        <div className="flex border-b border-gray-200 bg-white px-6 shrink-0">
          {[
            { key: 'blocks', label: 'Room Blocks' },
            { key: 'report', label: 'Report' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`mr-1 rounded-t-lg px-5 py-2.5 text-sm font-semibold transition-colors focus:outline-none ${
                activeTab === t.key
                  ? 'border-b-2 border-rose-600 text-rose-700 bg-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            TAB: Room Blocks
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'blocks' && (
          <>
            {/* ── filters + add button ── */}
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-3 items-end shrink-0">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500">Filter by Date</label>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 focus:border-rose-400 focus:ring-2 focus:ring-rose-300/30 focus:outline-none"
                />
              </div>
              <div className="flex flex-col gap-1" style={{ minWidth: 220 }}>
                <label className="text-xs font-medium text-gray-500">Filter by Room</label>
                <Select
                  options={classrooms.map((c) => ({ value: c.classroomId, label: `${c.name} (${c.floor})` }))}
                  value={filterClassroomId ? classrooms.filter(c => String(c.classroomId) === String(filterClassroomId)).map(c => ({ value: c.classroomId, label: `${c.name} (${c.floor})` }))[0] || null : null}
                  onChange={(opt) => setFilterClassroomId(opt ? String(opt.value) : '')}
                  isClearable
                  placeholder="All rooms"
                  styles={selectStyles}
                />
              </div>
              <button
                onClick={loadBlocks}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                title="Refresh list"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                onClick={openCreate}
                className="ml-auto rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-rose-700 transition-colors flex items-center gap-1.5"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                </svg>
                Add Block
              </button>
            </div>

            {/* ── inline form ── */}
            {showForm && (
              <div className="px-6 py-4 border-b border-rose-100 bg-rose-50 shrink-0">
                <h3 className="text-sm font-semibold text-rose-800 mb-3">
                  {editingId ? 'Edit Block' : 'New Block'}
                </h3>
                {formErrors.length > 0 && (
                  <ul className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 space-y-0.5">
                    {formErrors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Room <span className="text-red-500">*</span></label>
                    <Select
                      options={classrooms.map((c) => ({ value: c.classroomId, label: `${c.name} (${c.floor})` }))}
                      value={form.classroomId ? classrooms.filter(c => String(c.classroomId) === String(form.classroomId)).map(c => ({ value: c.classroomId, label: `${c.name} (${c.floor})` }))[0] || null : null}
                      onChange={(opt) => setForm((prev) => ({ ...prev, classroomId: opt ? String(opt.value) : '' }))}
                      isClearable
                      placeholder="Select room…"
                      styles={selectStyles}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Date <span className="text-red-500">*</span></label>
                    <input type="date" name="date" value={form.date} onChange={handleChange} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-rose-400 focus:ring-2 focus:ring-rose-300/30 focus:outline-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Start Time <span className="text-red-500">*</span></label>
                    <input type="time" name="startTime" value={form.startTime} onChange={handleChange} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-rose-400 focus:ring-2 focus:ring-rose-300/30 focus:outline-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">End Time <span className="text-red-500">*</span></label>
                    <input type="time" name="endTime" value={form.endTime} onChange={handleChange} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-rose-400 focus:ring-2 focus:ring-rose-300/30 focus:outline-none" />
                  </div>
                  <div className="flex flex-col gap-1 sm:col-span-2">
                    <label className="text-xs font-medium text-gray-600">Title <span className="text-red-500">*</span></label>
                    <input type="text" name="title" value={form.title} onChange={handleChange} placeholder='e.g. "IEEE Seminar", "AC Maintenance"' className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-rose-400 focus:ring-2 focus:ring-rose-300/30 focus:outline-none" />
                  </div>
                  <div className="flex flex-col gap-1 sm:col-span-2">
                    <label className="text-xs font-medium text-gray-600">Description</label>
                    <textarea name="description" value={form.description} onChange={handleChange} rows={2} placeholder="Additional details about the block reason…" className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm resize-none focus:border-rose-400 focus:ring-2 focus:ring-rose-300/30 focus:outline-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Organizer</label>
                    <input type="text" name="organizer" value={form.organizer} onChange={handleChange} placeholder="Person or organisation name" className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-rose-400 focus:ring-2 focus:ring-rose-300/30 focus:outline-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600">Contact No</label>
                    <input type="text" name="contactNo" value={form.contactNo} onChange={handleChange} placeholder="Phone number" className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-rose-400 focus:ring-2 focus:ring-rose-300/30 focus:outline-none" />
                  </div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <button onClick={closeForm} className="rounded-lg border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="rounded-lg bg-rose-600 px-5 py-1.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60 transition-colors">
                    {saving ? 'Saving…' : editingId ? 'Update' : 'Save'}
                  </button>
                </div>
              </div>
            )}

            {/* ── list ── */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {loading ? (
                <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Loading…</div>
              ) : blocks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 opacity-30 mb-2">
                    <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">No blocks found. Add one to mark a room as unavailable.</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {blocks.map((b) => (
                    <div key={b.blockId} className="rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                      <div className="flex items-start gap-4 px-5 py-4">
                        <div className="mt-0.5 w-1 h-12 rounded-full bg-rose-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-900 truncate">{b.title}</span>
                            <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                              {b.classroomName} · {b.floor}
                            </span>
                          </div>
                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-gray-400"><path fillRule="evenodd" d="M4 1.75a.75.75 0 0 1 1.5 0V3h5V1.75a.75.75 0 0 1 1.5 0V3h.25A2.75 2.75 0 0 1 15 5.75v7.5A2.75 2.75 0 0 1 12.25 16H3.75A2.75 2.75 0 0 1 1 13.25v-7.5A2.75 2.75 0 0 1 3.75 3H4V1.75ZM3.75 4.5c-.69 0-1.25.56-1.25 1.25v.25h11v-.25c0-.69-.56-1.25-1.25-1.25H3.75ZM2.5 7.5v5.75c0 .69.56 1.25 1.25 1.25h8.5c.69 0 1.25-.56 1.25-1.25V7.5h-11Z" clipRule="evenodd" /></svg>
                              {formatDisplayDate(String(b.date).slice(0, 10))}
                            </span>
                            <span className="flex items-center gap-1">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-gray-400"><path fillRule="evenodd" d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1ZM4.75 8a.75.75 0 0 1 .75-.75h2.25V4.75a.75.75 0 0 1 1.5 0v3.25A.75.75 0 0 1 8.5 8.75h-3A.75.75 0 0 1 4.75 8Z" clipRule="evenodd" /></svg>
                              {to12h(String(b.startTime).slice(0, 5))} – {to12h(String(b.endTime).slice(0, 5))}
                            </span>
                            {b.organizer && (
                              <span className="flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-gray-400"><path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0c-.22.578.254 1.139.872 1.139h9.47Z" /></svg>
                                {b.organizer}{b.contactNo && <span className="text-gray-400"> · {b.contactNo}</span>}
                              </span>
                            )}
                          </div>
                          {b.description && <p className="mt-1.5 text-xs text-gray-500 leading-relaxed line-clamp-2">{b.description}</p>}
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button onClick={() => openEdit(b)} className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 hover:text-indigo-600 transition-colors" title="Edit">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5"><path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L6.75 6.774a2.75 2.75 0 0 0-.596.892l-.848 2.047a.75.75 0 0 0 .98.98l2.047-.848a2.75 2.75 0 0 0 .892-.596l4.261-4.263a1.75 1.75 0 0 0 0-2.474ZM4.75 14A2.75 2.75 0 0 1 2 11.25v-4.5A2.75 2.75 0 0 1 4.75 4h1.5a.75.75 0 0 1 0 1.5h-1.5c-.69 0-1.25.56-1.25 1.25v4.5c0 .69.56 1.25 1.25 1.25h4.5c.69 0 1.25-.56 1.25-1.25v-1.5a.75.75 0 0 1 1.5 0v1.5A2.75 2.75 0 0 1 9.25 14h-4.5Z" /></svg>
                          </button>
                          <button onClick={() => handleDelete(b.blockId)} className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors" title="Delete">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5"><path fillRule="evenodd" d="M5 3.25V4H2.75a.75.75 0 0 0 0 1.5h.3l.815 8.15A1.5 1.5 0 0 0 5.357 15h5.285a1.5 1.5 0 0 0 1.493-1.35l.815-8.15h.3a.75.75 0 0 0 0-1.5H11v-.75A2.25 2.25 0 0 0 8.75 1h-1.5A2.25 2.25 0 0 0 5 3.25Zm2.25-.75a.75.75 0 0 0-.75.75V4h3v-.75a.75.75 0 0 0-.75-.75h-1.5ZM6.05 6a.75.75 0 0 1 .787.713l.275 5.5a.75.75 0 0 1-1.498.075l-.275-5.5A.75.75 0 0 1 6.05 6Zm3.9 0a.75.75 0 0 1 .712.787l-.275 5.5a.75.75 0 0 1-1.498-.075l.275-5.5a.75.75 0 0 1 .786-.713Z" clipRule="evenodd" /></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            TAB: Report
        ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'report' && (
          <>
            {/* ── date range controls ── */}
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-3 items-end shrink-0">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500">Start Date</label>
                <input
                  type="date"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 focus:border-rose-400 focus:ring-2 focus:ring-rose-300/30 focus:outline-none"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500">End Date</label>
                <input
                  type="date"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 focus:border-rose-400 focus:ring-2 focus:ring-rose-300/30 focus:outline-none"
                />
              </div>
              <button
                onClick={loadReport}
                disabled={reportLoading}
                className="rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60 transition-colors flex items-center gap-1.5"
              >
                {reportLoading ? (
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/><path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clipRule="evenodd"/></svg>
                )}
                {reportLoading ? 'Loading…' : 'Generate'}
              </button>
              {reportFetched && reportBlocks.length > 0 && (
                <button
                  onClick={downloadExcel}
                  className="ml-auto rounded-lg bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors flex items-center gap-1.5"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z"/>
                    <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z"/>
                  </svg>
                  Download Excel
                </button>
              )}
            </div>

            {/* ── report table ── */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {!reportFetched && !reportLoading && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 opacity-30 mb-2">
                    <path fillRule="evenodd" d="M2.625 6.75a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875 0A.75.75 0 0 1 8.25 6h12a.75.75 0 0 1 0 1.5h-12a.75.75 0 0 1-.75-.75ZM2.625 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0ZM7.5 12a.75.75 0 0 1 .75-.75h12a.75.75 0 0 1 0 1.5h-12A.75.75 0 0 1 7.5 12Zm-4.875 5.25a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875 0a.75.75 0 0 1 .75-.75h12a.75.75 0 0 1 0 1.5h-12a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm">Select a date range and click <strong>Generate</strong> to view the report.</p>
                </div>
              )}
              {reportLoading && (
                <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Loading report…</div>
              )}
              {reportFetched && !reportLoading && reportBlocks.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 opacity-30 mb-2">
                    <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">No room blocks found in the selected date range.</span>
                </div>
              )}
              {reportFetched && !reportLoading && reportBlocks.length > 0 && (
                <>
                  <p className="text-xs text-gray-500 mb-3">{reportBlocks.length} block{reportBlocks.length !== 1 ? 's' : ''} found — sorted newest first</p>
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="min-w-full text-xs">
                      <thead className="bg-rose-50 text-rose-800">
                        <tr>
                          {['Date', 'Room', 'Floor', 'Time', 'Title', 'Organizer', 'Contact'].map((h) => (
                            <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap border-b border-rose-100">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {reportBlocks.map((b) => (
                          <tr key={b.blockId} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap text-gray-700">{formatDisplayDate(String(b.date).slice(0, 10))}</td>
                            <td className="px-3 py-2 whitespace-nowrap font-semibold text-gray-800">{b.classroomName}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-500">{b.floor}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-700">{to12h(String(b.startTime).slice(0,5))} – {to12h(String(b.endTime).slice(0,5))}</td>
                            <td className="px-3 py-2 text-gray-800 max-w-[160px] truncate" title={b.title}>{b.title}</td>
                            <td className="px-3 py-2 text-gray-600">{b.organizer || '—'}</td>
                            <td className="px-3 py-2 text-gray-600">{b.contactNo || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
}
